import { map } from 'lodash-es';
import { Case, DraftCase } from '@/api/apis/case';
import { PaginatedBody, Uuid } from '@/api/types';
import { queryClient } from '@/queries';
import { caseQueries } from '@/queries/case/case.query';
import { CASE_KEYS, caseQueryKeys } from '@/queries/case/case.keys';

export const updateCaseField = <T extends keyof Case>(caseId: Uuid, field: T, value: Case[T]) => {
  const itemUpdater = (prevData?: Case) => {
    if (!prevData) return prevData;
    return {
      ...prevData,
      [field]: value,
    };
  };

  queryClient.setQueriesData({ queryKey: caseQueries.item(caseId).queryKey }, itemUpdater);
};

export const updateCaseInInfiniteList =
  (caseInfo: Case | DraftCase) => (prevData?: { pages: PaginatedBody<Case | DraftCase>[]; pageParams: unknown[] }) => {
    if (!prevData) return prevData;

    return {
      ...prevData,
      pages: prevData.pages.map((page) => {
        const items = map(page.items, (c) => (c.caseId === caseInfo.caseId ? caseInfo : c));
        return {
          ...page,
          items,
        };
      }),
    };
  };

export const addCaseToList =
  (caseInfo: Case, position: 'start' | 'end' = 'end') =>
  (prevCasesData?: PaginatedBody<Case>) => {
    if (!prevCasesData) return prevCasesData;

    const newCase = { ...caseInfo, createdAt: new Date().toISOString() };

    return {
      ...prevCasesData,
      items: position === 'start' ? [newCase, ...prevCasesData.items] : [...prevCasesData.items, newCase],
    };
  };

export const removeCaseFromInfiniteList =
  (caseId: Uuid) => (prevData?: { pages: PaginatedBody<Case>[]; pageParams: unknown[] }) => {
    if (!prevData) return prevData;

    return {
      ...prevData,
      pages: prevData.pages.map((page) => ({
        ...page,
        items: page.items.filter((c) => c.caseId !== caseId),
        totalItems: page.totalItems - 1,
      })),
    };
  };

export const removeCaseFromAllListCaches = (caseId: Uuid) => {
  queryClient.setQueriesData({ queryKey: caseQueryKeys.getInfiniteList() }, removeCaseFromInfiniteList(caseId));
};

export const invalidateInfiniteCaseListQueries = () => {
  queryClient.invalidateQueries({ queryKey: caseQueryKeys.getInfiniteList(), exact: false });
};

export const setCaseItemQuery = (caseId: string, caseInfo?: DraftCase | Case) => {
  if (!caseInfo) return;
  // List payload trimmed (no reallocationData) - skip if full item cached, else seed for fast paint and invalidate so useCaseByIdQuery refetches.
  const existing = queryClient.getQueryData<DraftCase | Case>(caseQueryKeys.getItem(caseId));
  if (existing) return;
  queryClient.setQueryData(caseQueryKeys.getItem(caseId), caseInfo);
  queryClient.invalidateQueries({ queryKey: caseQueryKeys.getItem(caseId) });
};

export const invalidateCaseItemQueries = async (caseId: string) => {
  await queryClient.invalidateQueries({ queryKey: caseQueries.item(caseId).queryKey });
  await queryClient.invalidateQueries({ queryKey: caseQueries.caseResourcesData(caseId).queryKey });
  await queryClient.invalidateQueries({ queryKey: caseQueries.logs(caseId).queryKey });
  await queryClient.invalidateQueries({ queryKey: caseQueries.activityLog(caseId).queryKey });
};

export const invalidateCaseLogs = (caseId: string) =>
  queryClient.invalidateQueries({ queryKey: [...caseQueryKeys.getItem(caseId), CASE_KEYS.logs] });

export const getCachedCaseListItems = (): Case[] =>
  queryClient
    .getQueriesData<{ pages: PaginatedBody<Case>[] }>({ queryKey: caseQueryKeys.getInfiniteList() })
    .flatMap(([, data]) => data?.pages.flatMap((page) => page.items) ?? []);
